import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import * as Styled from './styled';
import * as GStyled from '../../styles/components';
import { ButtonsBar } from '../../styles/components';
import { Button, StyledText } from '@leasequery/lq.react-component-library';
import { LeftSideNavContainer } from '../../components/page-containers/LeftSideNavContainer';
import {
  SideNav,
  SideNavItemSelectEvent,
  SideNavCategory,
  SideNavCategoryTypeName,
} from '../../components/side-nav';
import Popover from '../../components/popover/Popover';
import { PopoverState, PopoverController } from '../../components/popover';
import RuleBuilder from './rule-builder/RuleBuilder';
import { ReportField } from '../../models/reportField';
import FieldPopoverContent from './field-popover-content/FieldPopoverContent';
import { RuleBuilderField } from './rule-builder';
import {
  DATE_TIME_FORMAT,
  REPORT_CURRENCY_SELECT_OPTIONS,
} from '../../constants';
import { useReportCategoryReducer, useRuleBuilderReducer } from './reducers';
import { ReportType, StringOrComponent } from '../../models';
import { useAuth, useInprogressReports } from '../../context';
import {
  fetchReportSnapshot,
  fetchReportFieldsWithCache,
  RunReportResponse,
  fetchSavedReport,
  fetchFavoriteFieldsWithCache,
  fetchRecentFields,
  fetchSingleStandardReportWithCache,
  fetchReportTypeSettingWithCache,
  fetchCustomCalendarWithCache,
  fetchSharedReport,
} from '../../api';
import { createRuleBuilderDefaultFields } from '../helpers';
import { useRouter } from 'next/router';
import { runCustomReport } from './helpers/runCustomReport';
import { ReportSettingState } from '../find-report/report-setting';
import {
  checkIfHasEditingRuleBuilderField,
  runStandardReport,
  objectsAreEqual,
  areFieldsEqual,
} from '../find-report/helpers';
import ReportSetting from '../find-report/report-setting/ReportSetting';
import { fetchReportDefaultFieldsWithCache } from '../../api/report/fetchReportDefaultFields';
import withClient from '../../components/with-client/withClient';
import { DisableSelectWrapper } from '../../styles/components';
import { DropResult } from 'react-beautiful-dnd';
import RuleBuilderDragDropContext, {
  FIELDS_DROPPABLE_ID,
  RULEBUILDER_DROPPABLE_ID,
} from './rule-builder/rule-builder-dragdrop-context/RuleBuilderDragDropContext';
import { getFieldIdFromReportFieldDraggableId } from './rule-builder/rule-builder-dragdrop-context/helpers';
import { createReportFieldKey, noop } from '../../util';
import { createReportSettingStateFromReportSnapshot } from '../find-report/report-setting/helpers/createReportSettingStateFromReportSnapshot';
import { createReportSettingStateFromSavedReport } from '../find-report/report-setting/helpers/createReportSettingStateFromSavedReport';
import { format } from 'date-fns';
import { NoItemsToDisplay } from '../../components/side-nav/sub-category-section/NoItemsToDisplay';
import {
  ReportNotifications,
  ActionMenu,
  Dialog,
  Icon,
  ActionMenuCompoundedComponent,
} from '../../components';
import ReportDateSetting from '../find-report/report-setting/report-date-setting/ReportDateSetting';
import { useReportContext } from '../../context/ReportContext';
import { useIsFrs102Enabled } from '../../util/hooks/useIsFrs102Enabled';
import { useRunReportErrorHandler } from '../../util/hooks/useRunReportErrorHandler';
import { replaceIfrsLabels } from '../../util/frs102Helper';
import { HybridReportData } from '../../models';

const EMPTY_REPORT_SETTING_STATE: ReportSettingState = {
  selectedCurrency: REPORT_CURRENCY_SELECT_OPTIONS[2],
  isValid: true,
  reportDate: format(Date.now(), DATE_TIME_FORMAT.FULL_DATE),
  reportName: '',
  reportDescription: '',
};

function CreateReport(): JSX.Element {
  const router = useRouter();
  const { user } = useAuth();

  const {
    scheduledReportSettingState,
    setScheduledReportSettingState,
    shouldSaveAndContinue,
    hybridReportTransferData,
    setHybridReportTransferData,
  } = useReportContext();

  const {
    ishybridReportString = 'false',
    initialSavedReportId = 0,
  } = router.query;

  const [isShowNotification, setIsShowNotification] = useState(false);
  const [
    isShowCreateReportErrorNotification,
    setIsShowCreateReportErrorNotification,
  ] = useState(false);
  const onApiError = useCallback((): void => {
    setIsShowNotification(false);
    setIsShowCreateReportErrorNotification(true);
  }, []);
  const { errorMessage: runReportErrorMessage, resetError } = useRunReportErrorHandler(onApiError);
  
  // Capture the hybrid report data once on mount to prevent reactive changes
  const initialHybridReportData = useRef(hybridReportTransferData);
  
  const createdHybridReportInfo = useMemo(
    () => ({
      isHybridReport: JSON.parse(ishybridReportString as string) as boolean,
      hybridReportData: initialHybridReportData.current,
      initialSavedReportId: JSON.parse(
        initialSavedReportId as string,
      ) as number,
    }),
    [ishybridReportString, initialSavedReportId],
  );
  const [isHybridReport, setIsHybridReport] = useState<boolean>(
    createdHybridReportInfo.isHybridReport,
  );
  const [hybridReportData, setHybridReportData] = useState<HybridReportData>(
    createdHybridReportInfo.hybridReportData,
  );

  const [reportSettingState, setReportSettingState] = useState(
    hybridReportData?.reportSettingState || EMPTY_REPORT_SETTING_STATE,
  );

  //#region Unsaved Changes
  const [initialSettingsToCompare, setInitialSettingsToCompare] = useState(
    hybridReportData?.reportSettingState || EMPTY_REPORT_SETTING_STATE,
  );
  const [initialFieldsToCompareTo, setInitialFieldsToCompareTo] =
    useState(null);
  const [reportHasUnsavedChanges, setReportHasUnsavedChanges] = useState(false);
  const [fieldsHaveUnsavedChanges, setFieldsHaveUnsavedChanges] =
    useState(false);
  const [settingsHaveUnsavedChanges, setSettingsHaveUnsavedChanges] =
    useState(false);
  const [canShowRefresh, setCanShowRefresh] = useState(false);
  const [isRefreshed, setIsRefreshed] = useState(false);

  const actionMenuRef = useRef<ActionMenuCompoundedComponent>();
  
  // Clean up hybrid report transfer data from context after mount
  // This prevents stale data on subsequent navigations
  useEffect(() => {
    // Only clear if we had hybrid data from context
    if (initialHybridReportData.current) {
      // Clear after a short delay to ensure data is consumed
      const timer = setTimeout(() => {
        setHybridReportTransferData(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [setHybridReportTransferData]);

  useEffect(() => {
    setReportHasUnsavedChanges(
      fieldsHaveUnsavedChanges || settingsHaveUnsavedChanges,
    );
  }, [fieldsHaveUnsavedChanges, settingsHaveUnsavedChanges]);

  const resetUnsavedState = useCallback(() => {
    setFieldsHaveUnsavedChanges(false);
    setSettingsHaveUnsavedChanges(false);
  }, []);

  //#endregion

  const reportSettingChangeHandler = useCallback(
    (updatedReportSettingState: ReportSettingState) => {
      setReportSettingState(updatedReportSettingState);
      // temp unsavedChanges
      setSettingsHaveUnsavedChanges(
        !objectsAreEqual(initialSettingsToCompare, updatedReportSettingState),
      );
    },
    [initialSettingsToCompare],
  );

  const scheduleSettingChangeHandler = useCallback(
    (updatedReportSettingState: ReportSettingState) => {
      setScheduledReportSettingState(updatedReportSettingState);
    },
    [setScheduledReportSettingState],
  );

  const [
    {
      categories,
      selectedCategory,
      selectedSubCategory,
      reportFieldsMap,
      favoriteFieldsMap,
    },
    {
      setSelectedCategory,
      setSelectedSubCategory,
      filterSubCategoryByRuleBuilderFields,
      updateStandardFields,
      updateFavoriteFields,
      updateRecentFields,
    },
  ] = useReportCategoryReducer();
  const [
    { fields },
    {
      addRuleBuilderField,
      updateRuleBuilderFields,
      removeRuleBuilderField,
      addRuleToRuleBuilderField,
      cancelFieldEditing,
      saveEditingFields,
      removeRule,
      updateRule,
      turnOnRuleEditingMode,
    },
  ] = useRuleBuilderReducer();

  const { client, clientSetting } = useAuth();
  const clientId = client ? client.clientId : user.clientId;

  const [, { addNewInProgressReport }] = useInprogressReports();
  const [restartRuleBuilder, setRestartRuleBuilder] = useState<boolean>(false);
  const [isLoadingFields, setIsLoadingFields] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isFormLocked, setIsFormLocked] = useState(false);

  const loadSideNavFavoriteFields = useCallback(async () => {
    const favoritesFields = await fetchFavoriteFieldsWithCache(clientId);
    updateFavoriteFields(favoritesFields);
  }, [clientId, updateFavoriteFields]);

  const loadSideNavRecentFields = useCallback(async () => {
    const recentFields = await fetchRecentFields(clientId);
    updateRecentFields(recentFields);
  }, [clientId, updateRecentFields]);

  const loadSideNavStandardFields = useCallback(async () => {
    const standardFields = await fetchReportFieldsWithCache(clientId);
    updateStandardFields({ reportFields: standardFields });
  }, [clientId, updateStandardFields]);

  const FINANCIAL_CATEGORY_NAME = 'Financial';

  const isFrs102Enabled = useIsFrs102Enabled();

  const refreshFields = async () => {
    setIsLoadingFields(true);
    setIsSearching(false);
    const standardFields = await fetchReportFieldsWithCache(clientId, true);
    updateStandardFields({
      reportFields: standardFields,
      selectedCategoryName: FINANCIAL_CATEGORY_NAME,
    });
    setIsLoadingFields(false);
    setIsRefreshed(true);
  };

  useEffect(() => {
    setIsLoadingFields(true);
    const fetchData = async (): Promise<void> => {
      await Promise.allSettled([
        loadSideNavFavoriteFields(),
        loadSideNavRecentFields(),
        loadSideNavStandardFields(),
      ]);
      setIsLoadingFields(false);
    };
    fetchData();
  }, [
    loadSideNavFavoriteFields,
    loadSideNavRecentFields,
    loadSideNavStandardFields,
  ]);

  // unsaved changes listener
  useEffect(() => {
    if (!isLoadingFields && initialFieldsToCompareTo) {
      setFieldsHaveUnsavedChanges(
        !areFieldsEqual(fields, initialFieldsToCompareTo),
      );
    }
  }, [
    fields,
    initialFieldsToCompareTo,
    isLoadingFields,
    addRuleBuilderField,
    updateRuleBuilderFields,
    removeRuleBuilderField,
    addRuleToRuleBuilderField,
    cancelFieldEditing,
    saveEditingFields,
    removeRule,
    updateRule,
    turnOnRuleEditingMode,
  ]);

  // this effect will load in fields from API, separate out defaults, and set fields state
  useEffect(() => {
    (async (): Promise<void> => {
      let nextHybridReportData: HybridReportData =
        createdHybridReportInfo.hybridReportData;
      let nextIsHybridReport = createdHybridReportInfo.isHybridReport;
      const { reportSnapshotId, savedReportId, sharedReportId } = router.query;

      let nextReportSettingState = isHybridReport
        ? // mark nextReportSettingState as null to keep the initial reportSettingState
          null
        : // clean ReportSettingState before rebuilding it from snapshot or savedReport
          EMPTY_REPORT_SETTING_STATE;

      // Fetch all dependencies data
      const [
        initialReportSnapshot,
        savedReportDetail,
        sharedReportDetail,
        reportDefaultFields,
        customCalendarSettings,
      ] = await Promise.all([
        reportSnapshotId
          ? fetchReportSnapshot(parseInt(reportSnapshotId as string))
          : null,
        savedReportId
          ? fetchSavedReport(parseInt(savedReportId as string))
          : null,
        sharedReportId
          ? fetchSharedReport(parseInt(sharedReportId as string), clientId)
          : null,
        fetchReportDefaultFieldsWithCache(0, clientId),
        fetchCustomCalendarWithCache(clientId),
      ]);
      let savedStandardReportId = 0;
      let savedClientId = clientId;
      if (initialReportSnapshot) {
        savedStandardReportId = initialReportSnapshot.standardReportId;
        savedClientId = initialReportSnapshot.clientId;
      } else if (savedReportDetail) {
        savedStandardReportId =
          savedReportDetail.reportSetting?.standardReportId;
      } else if (sharedReportDetail) {
        savedStandardReportId =
          sharedReportDetail.reportSetting?.standardReportId;
      }

      // build default fields from hybridReport data or from snapshot or savedReportDetail
      const defaultFields = createdHybridReportInfo.isHybridReport
        ? [...(createdHybridReportInfo.hybridReportData?.ruleBuilderFields || [])]
        : createRuleBuilderDefaultFields(
            reportDefaultFields,
            initialReportSnapshot,
            savedReportDetail,
            sharedReportDetail,
          );

      // load report settings from snapshot or savedReport if any
      if (initialReportSnapshot || savedReportDetail || sharedReportDetail) {
        const [standardReport, reportTypeSetting] = savedStandardReportId
          ? await Promise.all([
              fetchSingleStandardReportWithCache(
                savedClientId,
                savedStandardReportId,
              ),
              fetchReportTypeSettingWithCache(savedClientId),
            ])
          : [null, null];
        nextReportSettingState = initialReportSnapshot
          ? createReportSettingStateFromReportSnapshot(
              initialReportSnapshot,
              reportTypeSetting,
            )
          : savedReportDetail
          ? createReportSettingStateFromSavedReport(
              savedClientId,
              savedReportDetail,
              standardReport,
              reportTypeSetting,
            )
          : createReportSettingStateFromSavedReport(
              // build report settings just like a saved report
              savedClientId,
              sharedReportDetail,
              standardReport,
              reportTypeSetting,
            );

        nextHybridReportData = {
          standardReport,
          reportTypeSetting,
          customCalendarSettings,
          ruleBuilderFields: defaultFields,
          reportSettingState: nextReportSettingState,
        };
        nextIsHybridReport = savedStandardReportId > 0;
      }

      // set report setting states
      setIsHybridReport(nextIsHybridReport);
      setHybridReportData(nextHybridReportData);
      if (nextReportSettingState) {
        setReportSettingState(nextReportSettingState);
        // capture initial state for unsaved change comparison
        setInitialSettingsToCompare(nextReportSettingState);
      }
      // set rule builder fields
      updateRuleBuilderFields(defaultFields);
      filterSubCategoryByRuleBuilderFields(defaultFields);

      // catpure initial fields to unsaved changes comparison
      setInitialFieldsToCompareTo(defaultFields);
    })();
  }, [
    filterSubCategoryByRuleBuilderFields,
    isHybridReport,
    router.query,
    updateRuleBuilderFields,
    restartRuleBuilder,
    clientId,
    createdHybridReportInfo.isHybridReport,
    createdHybridReportInfo.hybridReportData?.ruleBuilderFields,
    createdHybridReportInfo.hybridReportData,
  ]);

  const popoverControllerRef = useRef(null as PopoverController);
  const subCategoryPanelScrollHandler = useCallback((): void => {
    popoverControllerRef.current.reposition();
  }, []);

  const popoverOnControllerHandler = (controller: PopoverController): void => {
    popoverControllerRef.current = controller;
  };

  const unselectSubCategory = useMemo(
    () => (): void => {
      setSelectedSubCategory(null);
      popoverControllerRef.current.hidePopover();
    },
    [setSelectedSubCategory],
  );

  const subCategorySelectHandler = useCallback(
    (e: SideNavItemSelectEvent) => {
      setSelectedSubCategory(e.category as SideNavCategory<ReportField>);
      popoverControllerRef.current.showPopover(e.target as HTMLElement);
    },
    [setSelectedSubCategory],
  );

  const categorySelectHandler = useCallback(
    (e: SideNavItemSelectEvent) => {
      setSelectedCategory(e.category as SideNavCategory<ReportField>);
      unselectSubCategory();
    },
    [setSelectedCategory, unselectSubCategory],
  );

  const popoverStateChangeHandler = useCallback(
    (nextState: PopoverState) => {
      if (!nextState.isShow) {
        setSelectedSubCategory(null);
      }
    },
    [setSelectedSubCategory],
  );

  const addToRuleBuilderHandler = useCallback((): void => {
    const fieldKey = createReportFieldKey(selectedSubCategory.tag);
    const field = {
      ...selectedSubCategory.tag,
      fieldKey,
      rules: [],
    };
    addRuleBuilderField(field);
    unselectSubCategory();
    filterSubCategoryByRuleBuilderFields([...fields, field]);
  }, [
    addRuleBuilderField,
    fields,
    filterSubCategoryByRuleBuilderFields,
    selectedSubCategory,
    unselectSubCategory,
  ]);

  const removeRuleBuilderFieldHandler = useCallback(
    (field: RuleBuilderField): void => {
      removeRuleBuilderField(field);
      filterSubCategoryByRuleBuilderFields(
        // because field is an item in fields so we can use reference comparision here
        fields.filter((item) => item !== field),
      );
    },
    [fields, filterSubCategoryByRuleBuilderFields, removeRuleBuilderField],
  );

  const handleNotificationClose = (): void => {
    setIsShowNotification(false);
    setIsFormLocked(false);
  };

  const handleCreateReportErrorNotificationClose = (): void => {
    setIsShowCreateReportErrorNotification(false);
    setIsFormLocked(false);
    resetError();
  };

  const scheduleRunClickHandler = useCallback(async () => {
    actionMenuRef.current?.scheduleReportAction('add');
  }, []);

  const runReportClickHandler = useCallback(async () => {
    setIsFormLocked(true);
    resetError();

    let ranReportResult: RunReportResponse;

    if (isHybridReport && hybridReportData?.standardReport) {
      ranReportResult = await runStandardReport(
        client,
        hybridReportData.standardReport,
        reportSettingState,
        fields,
        undefined,
        reportSettingState.reportName,
        clientSetting.isCoaEnabled,
      );
    } else {
      ranReportResult = await runCustomReport(
        client,
        reportSettingState,
        fields,
        reportSettingState.reportName,
      );
    }

    if (ranReportResult) {
      setIsShowNotification(true);
      addNewInProgressReport(ranReportResult.id, ranReportResult.taskName);
      loadSideNavRecentFields();
    }

    const timer = setTimeout(() => {
      handleNotificationClose();
    }, 3000);
    return (): void => clearTimeout(timer);
  }, [
    addNewInProgressReport,
    client,
    clientSetting.isCoaEnabled,
    fields,
    hybridReportData?.standardReport,
    isHybridReport,
    loadSideNavRecentFields,
    reportSettingState,
    resetError,
  ]);


  const deletedFromFavoriteHandler = useCallback(
    (field: ReportField) => {
      if (
        selectedSubCategory.typeName === 'favoriteFields' &&
        createReportFieldKey(field) === selectedSubCategory.key
      ) {
        popoverControllerRef.current.hidePopover();
        setSelectedSubCategory(null);
      }
      loadSideNavFavoriteFields();
    },
    [loadSideNavFavoriteFields, selectedSubCategory, setSelectedSubCategory],
  );

  const isDisclosureReport = hybridReportData?.standardReport?.reportType === ReportType.disclosure;
  const isRuleBuilderHaveCustomFields = fields.length > 0 || isDisclosureReport;
  const hasEditingRuleBuilderField = checkIfHasEditingRuleBuilderField(fields);
  const dragEndHandler = useCallback(
    (result: DropResult) => {
      if (result?.destination?.droppableId !== RULEBUILDER_DROPPABLE_ID) {
        return;
      }
      const nextFields = [...fields];
      let sourceIndex = result.source.index;
      // add field to rule builder if dragged item is field
      if (result.source.droppableId.startsWith(FIELDS_DROPPABLE_ID)) {
        const fieldKey = getFieldIdFromReportFieldDraggableId(
          result.draggableId,
        );
        const field = {
          ...reportFieldsMap.get(fieldKey),
          fieldKey,
          rules: [],
        };
        nextFields.push(field);
        sourceIndex = nextFields.length - 1;
        filterSubCategoryByRuleBuilderFields(nextFields);
      }
      // re-order dropped item
      const items = nextFields.splice(sourceIndex, 1);
      nextFields.splice(result.destination.index, 0, ...items);
      updateRuleBuilderFields(nextFields);
    },
    [
      fields,
      filterSubCategoryByRuleBuilderFields,
      reportFieldsMap,
      updateRuleBuilderFields,
    ],
  );

  const subItemDisplayBuilder = useCallback(
    (category: SideNavCategory<ReportField>) => {
      const fieldKey = createReportFieldKey(category.tag);
      const isFavorite = favoriteFieldsMap.has(fieldKey);
      const categoryName = replaceIfrsLabels(
        category.name,
        isFrs102Enabled,
      );

      return (
        <Styled.ReportFieldItem>
          {categoryName}
          {isFavorite && <Icon name="filledStar" width="16" height="16" />}
        </Styled.ReportFieldItem>
      );
    },
    [favoriteFieldsMap, isFrs102Enabled],
  );

  const subItemListBuilder = useCallback(
    (
      category: SideNavCategory<ReportField>,
      defaultItemComponents: StringOrComponent[],
    ): StringOrComponent => {
      return category.items.length ? (
        <>{defaultItemComponents}</>
      ) : (
        <NoItemsToDisplay colorScheme="blue" subCategory={category} />
      );
    },
    [],
  );

  const isRunReportDisabled =
    isFormLocked ||
    !isRuleBuilderHaveCustomFields ||
    hasEditingRuleBuilderField ||
    !reportSettingState.isValid ||
    reportSettingState.pitIsInvalid;

  const isFuturePITDate =
    reportSettingState.pitErrors?.includes(
      'Please enter a non-future date or instead schedule a run in the future.',
    ) || reportSettingState.pitErrors?.length === 0;

  const isScheduleReportDisabled =
    !reportSettingState.isValid ||
    (reportSettingState.pitSwitchValue?.value && !isFuturePITDate);

  const categoriesToSearch = categories[0].items.filter(
    (x) => x.key === SideNavCategoryTypeName.Searchable,
  )[0]?.items;

  // take a copy of current state to build schedule state
  useEffect(() => {
    setScheduledReportSettingState(reportSettingState);
  }, [reportSettingState, setScheduledReportSettingState]);

  const { savedReportId } = router.query;

  const onCloseRefreshPanel = () => setCanShowRefresh(false);

  useEffect(() => {
    if (!isRefreshed) {
      setCanShowRefresh(selectedCategory?.name === FINANCIAL_CATEGORY_NAME);
    }
  }, [isSearching, selectedCategory?.name, isRefreshed]);

  return (
    <>
      <LeftSideNavContainer>
        <RuleBuilderDragDropContext
          onDragEnd={dragEndHandler}
          ruleBuilderFieldsLength={fields.length}
        >
          <SideNav
            isLoading={isLoadingFields}
            isDraggable={true}
            dropableId={FIELDS_DROPPABLE_ID}
            categories={categories}
            selectedCategory={selectedCategory}
            selectedSubCategory={selectedSubCategory}
            onCategorySelect={categorySelectHandler}
            onSubCategorySelect={subCategorySelectHandler}
            onSubcategoryPanelScroll={subCategoryPanelScrollHandler}
            colorScheme="blue"
            onItemDoubleClick={addToRuleBuilderHandler}
            subItemDisplayBuilder={subItemDisplayBuilder}
            subItemListBuilder={subItemListBuilder}
            isSearching={isSearching}
            setIsSearching={setIsSearching}
            categoriesToSearch={categoriesToSearch}
            refreshFieldsCallback={refreshFields}
            canShowRefresh={canShowRefresh}
            onCloseRefreshPanel={onCloseRefreshPanel}
          />

          <GStyled.ScrollableContainer>
            <DisableSelectWrapper>
              <ActionMenu
                ref={actionMenuRef}
                isSaveEnabled={!isRunReportDisabled}
                reportSettingState={reportSettingState}
                fields={fields}
                isHybridReport={isHybridReport}
                standardReport={hybridReportData?.standardReport}
                triggerRestart={(): void =>
                  setRestartRuleBuilder((current) => !current)
                }
                initialSavedReportId={
                  createdHybridReportInfo.initialSavedReportId
                }
                reportHasUnsavedChanges={reportHasUnsavedChanges}
                resetUnsavedState={resetUnsavedState}
              >
                <ReportDateSetting
                  reportType={
                    (hybridReportData?.standardReport
                      ? hybridReportData.standardReport.reportType
                      : ReportType.custom) as ReportType
                  }
                  isAllowPIT={
                    hybridReportData?.standardReport
                      ? hybridReportData.standardReport.isAllowPIT
                      : true // Enable PIT for custom report
                  }
                  isAllowSplitByAllocationForReport={
                    hybridReportData?.standardReport
                      ? hybridReportData.standardReport.isAllowSplitByAllocationForReport
                      : false
                  }
                  onChange={scheduleSettingChangeHandler}
                  reportSettingState={scheduledReportSettingState}
                  reportTypeSetting={hybridReportData?.reportTypeSetting}
                  customCalendarSettings={
                    hybridReportData?.customCalendarSettings
                  }
                />
              </ActionMenu>

              <Styled.CreateReportContainer>
                <ReportSetting
                  reportType={
                    (hybridReportData?.standardReport
                      ? hybridReportData.standardReport.reportType
                      : ReportType.custom) as ReportType
                  }
                  isAllowPIT={
                    hybridReportData?.standardReport
                      ? hybridReportData.standardReport.isAllowPIT
                      : true // Enable PIT for custom report
                  }
                  onChange={reportSettingChangeHandler}
                  reportSettingState={reportSettingState}
                  reportTypeSetting={hybridReportData?.reportTypeSetting}
                  customCalendarSettings={
                    hybridReportData?.customCalendarSettings
                  }
                />

                {!isDisclosureReport && (
                  <>
                    {isHybridReport && (
                      <>
                        <Styled.CreateReportIncludedFieldsTitleBar>
                          <div>
                            <Icon name="report" />
                          </div>
                          <StyledText fontWeight="medium">
                            Included Fields
                          </StyledText>
                        </Styled.CreateReportIncludedFieldsTitleBar>
                      </>
                    )}

                    <Styled.RuleBuilderWrapper>
                      {!isRuleBuilderHaveCustomFields && (
                        <GStyled.Text>
                          You must add a field to the report before you can run or
                          save.
                          <br />
                          <br />
                        </GStyled.Text>
                      )}
                      <RuleBuilder
                        fields={fields}
                        onRemoveField={removeRuleBuilderFieldHandler}
                        onAddRule={addRuleToRuleBuilderField}
                        onCancelFieldEditing={cancelFieldEditing}
                        onRemoveRuleClick={removeRule}
                        onEditRuleClick={turnOnRuleEditingMode}
                        onSaveEditingField={saveEditingFields}
                        onRuleChange={updateRule}
                      />
                    </Styled.RuleBuilderWrapper>
                  </>
                )}

                <ReportDateSetting
                  reportType={
                    (hybridReportData?.standardReport
                      ? hybridReportData.standardReport.reportType
                      : ReportType.custom) as ReportType
                  }
                  isAllowPIT={
                    hybridReportData?.standardReport
                      ? hybridReportData.standardReport.isAllowPIT
                      : true // Enable PIT for custom report
                  }
                  isAllowSplitByAllocationForReport={
                    hybridReportData?.standardReport
                      ? hybridReportData.standardReport.isAllowSplitByAllocationForReport
                      : false
                  }
                  onChange={reportSettingChangeHandler}
                  reportSettingState={reportSettingState}
                  reportTypeSetting={hybridReportData?.reportTypeSetting}
                  customCalendarSettings={
                    hybridReportData?.customCalendarSettings
                  }
                >
                  <ButtonsBar>
                    <Button
                      buttonType="secondary"
                      onClick={scheduleRunClickHandler}
                      disabled={isScheduleReportDisabled}
                    >
                      <Icon
                        name="schedule"
                        disabled={isScheduleReportDisabled}
                      />
                      <span>Schedule Run</span>
                    </Button>
                    <Button
                      buttonType="primary"
                      disabled={isRunReportDisabled}
                      onClick={runReportClickHandler}
                    >
                      Run Report
                    </Button>
                  </ButtonsBar>
                </ReportDateSetting>
              </Styled.CreateReportContainer>
            </DisableSelectWrapper>
          </GStyled.ScrollableContainer>
        </RuleBuilderDragDropContext>
      </LeftSideNavContainer>

      <Popover
        onController={popoverOnControllerHandler}
        onStateChange={popoverStateChangeHandler}
      >
        {selectedSubCategory && selectedSubCategory.tag && (
          <FieldPopoverContent
            field={selectedSubCategory.tag}
            onAddToRuleBuilderClick={addToRuleBuilderHandler}
            onAddedToFavorite={loadSideNavFavoriteFields}
            onDeletedFromFavorite={deletedFromFavoriteHandler}
          />
        )}
      </Popover>

      <Dialog
        isOpen={isShowNotification}
        onStateChange={noop}
        withoutCover={true}
      >
        <ReportNotifications
          notificationType="running"
          onClose={handleNotificationClose}
        />
      </Dialog>

      <Dialog
        isOpen={isShowCreateReportErrorNotification}
        onStateChange={noop}
        withoutCover={true}
        maxWidth="60rem"
      >
        <ReportNotifications
          notificationType="createReportError"
          onClose={handleCreateReportErrorNotificationClose}
          errorMessage={runReportErrorMessage}
        />
      </Dialog>

      {/* save and continue flow - empty cover while schedule modal loads */}
      <Dialog
        emptyCover={true}
        isOpen={shouldSaveAndContinue && !!savedReportId}
      />
    </>
  );
}

export default withClient(CreateReport);
